import { ChevronDown, Search, X } from "lucide-react";
import { type CSSProperties, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type SearchSelectOption = {
  value: string;
  label: string;
  selectedLabel?: string;
  keywords?: string;
  disabled?: boolean;
};

const normalizeSearch = (value: string) => value
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/đ/g, "d")
  .replace(/Đ/g, "D")
  .toLocaleLowerCase("vi-VN")
  .trim();

export function SearchSelect({
  value,
  options,
  onChange,
  placeholder = "Nhập mã hoặc tên để tìm",
  emptyText = "Không có kết quả phù hợp",
  disabled = false,
  onQueryChange,
  invalid = false,
  errorText
}: {
  value: string;
  options: SearchSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  onQueryChange?: (query: string) => void;
  invalid?: boolean;
  errorText?: string;
}) {
  const selected = options.find((option) => option.value === value);
  const selectedText = selected?.selectedLabel ?? selected?.label ?? "";
  const [query, setQuery] = useState(selectedText);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const inputId = useId();
  const menuId = `${inputId}-menu`;
  const inputName = useRef(`f_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`);
  const rootRef = useRef<HTMLDivElement>(null);
  const clearingForTypingRef = useRef(false);
  const [inputActivated, setInputActivated] = useState(false);

  useEffect(() => {
    if (clearingForTypingRef.current) {
      clearingForTypingRef.current = false;
      return;
    }
    setQuery(selectedText);
  }, [selectedText, value]);

  const filtered = useMemo(() => {
    const term = normalizeSearch(query);
    const selectedIsShown = selectedText === query;
    const matches = !term || selectedIsShown
      ? options
      : options.filter((option) => normalizeSearch(`${option.label} ${option.keywords ?? ""}`).includes(term));
    return matches.slice(0, 10);
  }, [options, query, selectedText]);

  const updateMenuPosition = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const margin = 8;
    const availableBelow = window.innerHeight - rect.bottom - margin;
    const availableAbove = rect.top - margin;
    const openAbove = availableBelow < 240 && availableAbove > availableBelow;
    const maxHeight = Math.max(150, Math.min(320, openAbove ? availableAbove : availableBelow));
    setMenuStyle({
      position: "fixed",
      left: Math.max(margin, rect.left),
      width: Math.min(rect.width, window.innerWidth - margin * 2),
      // Phải ghi đè bằng "auto" vì CSS gốc luôn có top. Nếu chỉ truyền
      // undefined, menu mở lên trên vẫn giữ top: calc(100% + 5px) và biến mất
      // khỏi màn hình ở dòng cuối của bảng.
      top: openAbove ? "auto" : rect.bottom + 4,
      bottom: openAbove ? window.innerHeight - rect.top + 4 : "auto",
      maxHeight,
      zIndex: 10000
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  const menu = open && !disabled ? createPortal(
    <div id={menuId} role="listbox" className="search-select-menu search-select-menu-portal" style={menuStyle}>
      {filtered.map((option) => (
        <button
          type="button"
          key={option.value}
          disabled={option.disabled}
          role="option"
          aria-selected={option.value === value}
          className={option.value === value ? "selected" : ""}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (option.disabled) return;
            onChange(option.value);
            setQuery(option.selectedLabel ?? option.label);
            setOpen(false);
          }}
        >
          {option.label}
        </button>
      ))}
      {!filtered.length && <span>{emptyText}</span>}
      {options.length > 10 && (!query.trim() || selectedText === query) && (
        <small>Nhập thêm ký tự để thu gọn danh sách.</small>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div ref={rootRef} className={`search-select ${disabled ? "disabled" : ""} ${invalid ? "field-invalid" : ""}`}>
      <Search size={17} className="search-select-icon" />
      <input
        id={inputId}
        name={inputName.current}
        type="text"
        role="combobox"
        aria-controls={menuId}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-invalid={invalid}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        readOnly={!inputActivated}
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        data-protonpass-ignore="true"
        data-keeper-ignore="true"
        value={query}
        disabled={disabled}
        placeholder={placeholder}
        onPointerDown={() => setInputActivated(true)}
        onFocus={() => {
          setInputActivated(true);
          setOpen(true);
          window.requestAnimationFrame(updateMenuPosition);
        }}
        onBlur={() => window.setTimeout(() => {
          setOpen(false);
          setInputActivated(false);
        }, 150)}
        onChange={(event) => {
          setQuery(event.target.value);
          onQueryChange?.(event.target.value);
          if (value) {
            clearingForTypingRef.current = true;
            onChange("");
          }
          setOpen(true);
        }}
      />
      {value ? (
        <button
          type="button"
          className="search-select-clear"
          title="Bỏ lựa chọn"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            onChange("");
            setQuery("");
            onQueryChange?.("");
            setOpen(true);
          }}
        >
          <X size={16} />
        </button>
      ) : (
        <ChevronDown size={17} className="search-select-chevron" />
      )}
      {invalid && errorText && <small className="field-error-text">{errorText}</small>}
      {menu}
    </div>
  );
}
